/** @jsx jsx */
import React, { Fragment } from 'react'
import { jsx } from 'theme-ui'

import { graphql } from 'gatsby'

import SEO from '../components/SEO'

import { Global } from '@emotion/core'
import { slickStyles } from '../components/shared/Carousel/ProductImageCarousel'
import { GenerateBlock } from './generateBlocks'

const PageTemplate = ({ data }) => {
  const {
    seo: { title, description, openGraphImage },
    blocks,
    ...rest
  } = data.contentfulPage

  return (
    <Fragment>
      <Global styles={slickStyles} />
      <SEO
        image={
          openGraphImage && openGraphImage.ogimg && openGraphImage.ogimg.src
        }
        title={title}
        description={description && description.description}
      />

      {blocks.map((i, k) => (
        <GenerateBlock key={k} block={i} {...rest} />
      ))}
    </Fragment>
  )
}

export const query = graphql`
  query($slug: String!) {
    contentfulPage(slug: { eq: $slug }) {
      slug
      landingPageCta
      seo {
        title
        description {
          description
        }
        openGraphImage {
          ogimg: resize(width: 700) {
            src
          }
        }
      }
      useAddToCartBtn
      productInfo {
        title
        shopifyProduct {
          availableForSale
          shopifyId
          productType
          variants {
            price
            compareAtPrice
            shopifyId
            availableForSale
          }
        }
      }
      blocks {
        __typename
        ... on ContentfulActionButtonComponent {
          sticky
          actionButton {
            ...ActionButton
          }
        }
        ... on ContentfulReviewCarouselComponent {
          title
          theme {
            textColor
            backgroundColor
          }
          layoutVariation
          reviews {
            title
            text {
              raw
            }
          }
        }
        ... on ContentfulHeroComponent {
          id
          title
          subTitle
          layoutVariation
          backgroundVideoUrl
          mobileBackgroundVideoUrl
          inline
          textColor
          actionButton {
            ...ActionButton
          }
          centeredHeaderImageMobile {
            file {
              url
            }
            fluid {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          centeredHeaderImage {
            file {
              url
            }
            fluid {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          mobileImage {
            fluid {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          image {
            fluid(maxWidth: 1800, quality: 85) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
        ... on ContentfulProductListComponent {
          id
          title
          showTitle
          featuredLayout
          productPricePreText
          infoCards {
            title
            position
            customPosition
            actionButton {
              ...ActionButton
            }
            description {
              description
            }
            image {
              fluid(maxWidth: 800) {
                ...GatsbyContentfulFluid_withWebp_noBase64
              }
            }
          }
          collection {
            id
            products {
              id
              shopifyId
              ...ShopifyProductDetails
            }
          }
          carouselMode
          reverseDisplayImages
        }
        ... on ContentfulLifestyleProductComponent {
          id
          title
          productVariant {
            price
            availableForSale
            product {
              shopifyId
              ...ShopifyProductDetails
            }
          }
          image {
            fluid(maxWidth: 600) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
        ... on ContentfulSideBySideComponent {
          id
          sideBySide {
            textAlign
            text {
              raw
              references {
                __typename

                ... on ContentfulActionButton {
                  contentful_id
                  ...ActionButton
                }
              }
            }
            reverse
            title
            fullHeightImage
            image {
              fluid(maxWidth: 600) {
                ...GatsbyContentfulFluid_withWebp_noBase64
              }
            }
          }
        }
        ... on ContentfulTextSectionComponent {
          text {
            raw
            references {
              __typename

              ... on ContentfulActionButton {
                contentful_id
                ...ActionButton
              }
            }
          }
          contentAlign
          actionButton {
            ...ActionButton
          }
          theme {
            textColor
            backgroundColor
          }
        }
        ... on ContentfulCtaGridComponent {
          title
          layoutVariation
          gridItems {
            title
            textColor
            actionButton {
              ...ActionButton
            }
            image {
              fluid(maxWidth: 700, quality: 40) {
                ...GatsbyContentfulFluid_withWebp_noBase64
              }
            }
          }
        }
        ... on ContentfulPost {
          slug
          heroImage {
            fluid(maxWidth: 1800, quality: 85) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          category
          title
          body {
            raw
          }
        }
        ... on ContentfulCarouselContainer {
          title
          showTitle
          carouselItem {
            toSlug
            title
            image {
              fluid(maxWidth: 800) {
                ...GatsbyContentfulFluid_withWebp_noBase64
              }
            }
          }
        }
        ... on ContentfulSpaceComponent {
          id
          name
          height
          heightOnMobile
          border
        }
      }
    }
  }
`

export default PageTemplate
